import { ReactNode, memo, useEffect, useState } from "react";

import CardMedia from "@mui/material/CardMedia";
import { SxProps, Theme } from "@mui/material/styles";

import { IS3MediaFile } from "./IS3MediaFileProps";
import KeyFrameMessage from "./NoKeyFrameMessage";

export interface S3ImageProps extends IS3MediaFile {
  isKeyframeLoaded?: boolean;
  width?: number;
  deviceStatus?: boolean;
  height?: number;
  sx?: SxProps<Theme>;
  loadingFallback?: ReactNode;
  setKeyframeLoaded?: (value: boolean) => void;
  onClick?: () => void;
  keyFrameMessage?: string;
}

const S3ImageComponent = ({
  s3Key,
  width,
  height,
  sx = [],
  isKeyframeLoaded,
  keyFrameMessage,
  loadingFallback,
  deviceStatus,
  setKeyframeLoaded,
  onClick,
}: S3ImageProps): JSX.Element => {
  const [imageUrl, setImageUrl] = useState("");
  const [doesFileExist, setDoesFileExist] = useState(false);

  const defaultSx = {
    objectFit: "fill",
    aspectRatio: "auto",
  };

  useEffect((): void => {
    getImage();
  }, [s3Key]);

  const getImage = async (): Promise<void> => {
    if (!s3Key) {
      setImageUrl("");

      setDoesFileExist(false);

      if (setKeyframeLoaded) {
        setKeyframeLoaded(false);
      }

      return;
    }

    setImageUrl(s3Key);

    setDoesFileExist(true);

    if (setKeyframeLoaded) {
      // checkIfFileExist has finished and therefore loading is complete
      setKeyframeLoaded(true);
    }
  };

  /**
   * If the image fails to load, show 'Please wait for the keyframe to retrieve' message
   */
  const handleImageError = async () => {
    setImageUrl("");

    setDoesFileExist(false);

    if (setKeyframeLoaded) {
      setKeyframeLoaded(false);
    }
  };

  const handleOnLoadedData = (): void => {
    if (setKeyframeLoaded) {
      setKeyframeLoaded(true);
    }
  };

  const sxStyles = [defaultSx, ...(Array.isArray(sx) ? sx : [sx])];

  return (
    <>
      {!isKeyframeLoaded && (
        <>
          {deviceStatus === false && !imageUrl ? (
            <KeyFrameMessage
              message="The device is offline. It can take some time"
              height={sxStyles ? sxStyles[1].height : "378px"}
            />
          ) : (
            (!doesFileExist || (!imageUrl && deviceStatus === undefined)) &&
            (loadingFallback || (
              <KeyFrameMessage
                message={
                  keyFrameMessage || "Please wait for the image to retrieve"
                }
                height={sxStyles ? sxStyles[1].height : "378px"}
              />
            ))
          )}
        </>
      )}

      {imageUrl && doesFileExist && (
        <CardMedia
          onClick={onClick}
          component="img"
          src={imageUrl}
          width={width}
          height={height}
          sx={sxStyles}
          onLoadedData={handleOnLoadedData}
          onError={handleImageError}
        />
      )}
    </>
  );
};

const S3Image = memo(S3ImageComponent);

export default S3Image;
